import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';

import ErrorModal from 'src/enosikit/components/ErrorModal';

/**
 * Surface-agnostic error UI shell. Renders a caller-supplied skeleton in the
 * background and layers a blocking retry/cancel modal above it. Contains only
 * display logic; callers compose retry and reset behaviour via the
 * `onCancelFunc` / `onRetryFunc` callbacks and choose the background skeleton
 * via `placeholderComponent`.
 *
 * Callers may supply an `error` object (for imperative hook-based errors) and
 * the component derives `{ message, stack }` details internally. Alternatively,
 * callers may pass a pre-built `details` object directly (used by the
 * class-based error boundary path, which also includes `componentStack`).
 * @param {object} props - Component props.
 * @param {string|null} props.bodyText - Optional custom modal body message.
 *   When null, ErrorModal renders its default body.
 * @param {object|null} props.details - Optional pre-built diagnostic payload
 *   rendered in the modal's "Show Details" section. Superseded by `error` when
 *   both are provided. When null and `error` is also null, the section is
 *   hidden.
 * @param {Error|null} props.error - Optional captured async error. When
 *   provided, `{ message, stack }` details are derived internally.
 * @param {Function|null} props.onCancelFunc - Invoked when the user dismisses
 *   the modal. The modal closes itself regardless of the callback's return.
 * @param {Function|null} props.onRetryFunc - Invoked when the user clicks
 *   retry. When null, the retry action is hidden by ErrorModal.
 * @param {React.ComponentType} props.placeholderComponent - Skeleton component
 *   rendered behind the modal for the surface being protected.
 * @param {boolean} props.retryDisabled - Disables retry when retry cannot
 *   succeed (for example, while a replay is in flight).
 * @returns {React.ReactNode} placeholder error UI.
 */
function PlaceholderErrorModal({
  bodyText = null, details = null, error = null, onCancelFunc = null, onRetryFunc = null,
  placeholderComponent: PlaceholderComponent, retryDisabled = false,
}) {
  const [isModalOpen, setIsModalOpen] = useState(true);

  const derivedDetails = useMemo(() => {
    if (error) {
      return {
        message: error.message || null,
        stack: error.stack || null,
      };
    }
    return details;
  }, [error, details]);

  const handleCancelFunc = () => {
    onCancelFunc?.();
    setIsModalOpen(false);
  };

  return (
    <>
      <PlaceholderComponent />
      {isModalOpen && (
        <ErrorModal
          bodyText={bodyText}
          details={derivedDetails}
          onCancelFunc={handleCancelFunc}
          onRetryFunc={onRetryFunc}
          retryDisabled={retryDisabled}
        />
      )}
    </>
  );
}

PlaceholderErrorModal.propTypes = {
  bodyText: PropTypes.string,
  details: PropTypes.shape({
    componentStack: PropTypes.string,
    message: PropTypes.string,
    stack: PropTypes.string,
  }),
  error: PropTypes.instanceOf(Error),
  onCancelFunc: PropTypes.func,
  onRetryFunc: PropTypes.func,
  placeholderComponent: PropTypes.elementType.isRequired,
  retryDisabled: PropTypes.bool,
};

export default PlaceholderErrorModal;
